import { elemSheet } from '@fullstory/shared/styleutils';
import { assertExhaustive } from '@fullstory/typeutils';
import type { FsCSSOMElement } from './fscssomelement';
import type { FsDocument } from './fsdocument';

// A dummy CSS rule we insert in place of rules that don't parse or are
// otherwise invalid.
export const dummyCssRule = 'html { }';

export const mediaPlaceholderRule = '@media { }';

export type FSRuleContainer = {
  insertRule: (cssText: string, index: number | undefined) => void;
  deleteRule: (index: number) => void;
  insertFromRule: (next: FSCssRule, idx: number) => FsCSSOMGroup;
  cssRules: FSCssRule[];
};

interface FSBasicCssRule {
  type: 'rule';
  cssText: string | undefined;
  changes?: Map<string, string>;
}

type FSCssRule = FSBasicCssRule | FsCSSOMGroup;

export type RuleContainer = FSRuleContainer | CSSStyleSheet | CSSGroupingRule;

// Virtualized representation of the dynamically constructed styles
// in a stylesheet. This doesn't include the rules represented in
// the DOM itself.
export class FsCSSOMSheet {
  public ownerNodes: Set<FsCSSOMElement>;

  /**
   * Whether the sheets associated with this CSSOM needs to be fully reset
   * (i.e. during flush, we need to wipe all the CSSOM rules from the dom stylesheets)
   */
  private _needsReset = false;

  /**
   * This holds a list of all the stylesheets that we have already updated,
   *   so that when we're flushing we don't need to do unnecessary work.
   * If our _insertRules changes and we don't apply it to these sheets, we need to clear this
   */
  private _upToDateSheets = new WeakSet<CSSStyleSheet>();

  // Maps id of a CSSStyleRule to a map of the changes made to that rule.
  // The changes here are additionally in _insertedRules.
  private ruleUpdates = new Map<number, FSBasicCssRule>();

  constructor(private _ownerDoc: FsDocument, ownerNodeList: FsCSSOMElement[]) {
    this.ownerNodes = new Set<FsCSSOMElement>(ownerNodeList);
  }

  // Possibly sparse array of css rules that have been dynamically inserted
  private _insertedRules: FSCssRule[] = [];

  get cssRules(): FSCssRule[] {
    return this._insertedRules;
  }

  // Return a list of real CSSOM sheets corresponding to this virtual one.
  // Missing sheets are omitted from the returned result.
  private _realSheets(): CSSStyleSheet[] {
    const sheets: CSSStyleSheet[] = [];
    for (const fsnode of this.ownerNodes) {
      const tag = fsnode.tag;
      if (tag !== 'link' && tag !== 'style') {
        throw new Error(`Invalid ownerNode tag: ${tag}`);
      }
      const elem = fsnode.dom as HTMLLinkElement | HTMLStyleElement | null;
      if (!elem) {
        continue;
      }
      const sheet = elemSheet(elem);
      if (sheet) {
        sheets.push(sheet);
      }
    }
    return sheets;
  }

  private _disabled = false;
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    if (value !== this._disabled) {
      this._onSheetChange();
      this._clearUpToDateSheets();
    }
    this._disabled = value;
  }

  getGroupByPath(path: number[]): FSRuleContainer {
    let current: FSRuleContainer = this;
    for (const idx of path) {
      let next = current.cssRules[idx];

      if (!next) {
        next = current.insertFromRule(next, idx);
      }

      switch (next.type) {
        case 'rule':
          next = current.insertFromRule(next, idx);
          break;
        case 'group':
          // do nothing, next is already a group
          break;
        default:
          assertExhaustive(next, 'unhandled FSCssRule type');
      }

      current = next;
    }

    return current;
  }

  insertFromRule(next: FSCssRule | FsCSSOMGroup, idx: number): FsCSSOMGroup {
    const group = new FsCSSOMGroup(this, next);
    this.cssRules[idx] = group;
    return group;
  }

  insertRule(rawRule: string, index = 0, target?: FsCSSOMGroup) {
    let rule = rawRule;

    // If we can't parse this rule (for example, because it's an MS-specific
    // rule and we're not playing back on Edge), just insert a trivial one in
    // its place. This ensures that subsequent calls to insertStyleRule() won't
    // end up inserting rules in the wrong order or just failing to insert them
    // at all. Note that we do the same thing for other failure modes in
    // insertRule().
    try {
      rule = this._ownerDoc.inlineCssRewriter.rewriteStyleRuleText(rule);
    } catch (e) {
      console.log(`Failed to rewrite rule '${rule}': ${e}`);
      rule = dummyCssRule;
    }

    const fsRule: FSBasicCssRule = {
      type: 'rule',
      cssText: rule,
      changes: new Map<string, string>(),
    };
    this.insertIntoCSSRules(fsRule, index, true, target);

    this._onSheetChange();
    this._clearUpToDateSheets();
  }

  updateRuleById(updates: { [key: string]: string }, id: number): void {
    const ruleId = id;

    if (!this.ruleUpdates.has(ruleId)) {
      return;
    }

    const rule = this.ruleUpdates.get(ruleId)?.changes;

    for (const [cssKey, value] of Object.entries(updates)) {
      const key = this.cssCamelToKebabCase(cssKey);
      rule?.set(key, value);
    }

    this._onSheetChange();
    this._clearUpToDateSheets();
  }

  updateRuleByIndices(
    updates: { [key: string]: string },
    index: number,
    pathToRule: number[] = [],
  ): void {
    // We are accessing this rule for the first time
    let fsRule: FSCssRule = {
      type: 'rule',
      cssText: '',
      changes: new Map<string, string>(),
    };

    const parentRule = this.getGroupByPath(pathToRule);
    // Was this a rule that was inserted? If so, we don't want to insert it, but assign it an ID for future use
    if (parentRule.cssRules[index]) {
      const rule = parentRule.cssRules[index];
      if (rule.type === 'rule') {
        fsRule = rule;
      }
    } else {
      this.insertIntoCSSRules(fsRule, index, undefined, parentRule);
    }

    const ruleId = this.setNextRuleId(fsRule);

    this.updateRuleById(updates, ruleId);
  }

  // NOTE: this needs to mirror the id generation in stylesheetwatcher
  private setNextRuleId(rule: FSBasicCssRule): number {
    const id = this._ownerDoc.styleRuleIdCounter++;
    this._ownerDoc.ruleOwners.set(id, this);
    this.ruleUpdates.set(id, rule);

    return id;
  }

  private insertIntoCSSRules(
    rule: FSCssRule,
    index: number,
    isInsert = false,
    target?: FSRuleContainer,
  ): void {
    const targetList: FSCssRule[] = (target ?? this).cssRules;

    if (isInsert) {
      if (index > targetList.length) {
        // We failed to record previous rules; or they're implied by
        // text in the DOM.
        targetList[index] = rule;
      } else {
        targetList.splice(index, 0, rule);
      }
    } else {
      targetList[index] = rule;
    }
  }

  deleteRule(index: number) {
    this._insertedRules.splice(index, 1);
    this._onSheetChange();
    this._clearUpToDateSheets();
  }

  private _clearUpToDateSheets() {
    this._upToDateSheets = new WeakSet<CSSStyleSheet>();
  }

  /**
   * This resets the rules associated with this stylesheet
   */
  reset() {
    this._needsReset = true;
    this._insertedRules = [];
    this._clearUpToDateSheets();
    this._onSheetChange();
  }

  flush() {
    for (const node of this.ownerNodes) {
      const ownerElement = node.dom as HTMLStyleElement | HTMLLinkElement | null;
      if (!ownerElement) {
        continue;
      }
      const oldSheet = elemSheet(ownerElement);
      // since this will clear our sheets in actual DOM, skip if sheet is up to date
      if (!oldSheet || this._upToDateSheets.has(oldSheet)) {
        continue;
      }

      // Replace ownerElement.sheet with a clean copy by mutating the
      // part of the DOM that generates it.
      const ownerTag = node.tag;
      switch (ownerTag) {
        case 'style': {
          const text = document.createTextNode(' ');
          ownerElement.appendChild(text);
          ownerElement.removeChild(text);
          break;
        }
        case 'link': {
          // Left unimplemented for now. Resetting link elements
          // with `ownerElement.href = ownerElement.href` behaves
          // differently on different browsers; and afaik css-in-js
          // libraries always use <style> elements instead.
          break;
        }
        default:
          throw new Error(`A <${ownerTag}> node shouldn't have a stylesheet.`);
      }

      if (this._needsReset) {
        // we need to reset any rule that may have been set by the text content of this node
        this._needsReset = false;
        const newSheet = elemSheet(ownerElement);
        if (!newSheet) {
          console.warn(`Could not find stylesheet after obtaining a clean copy`);
        } else {
          for (let i = newSheet.cssRules.length - 1; i >= 0; i--) {
            newSheet.deleteRule(i);
          }
        }
      }
    }

    // We will only add rules to sheets that need to be modified (ch97298)
    // (i.e. exclude sheets that we already considered to be up to date)
    const sheetsToApply = this._realSheets().filter(sheet => !this._upToDateSheets.has(sheet));
    // Now insert our rules into sheets.
    // TODO: We could be smarter about this, e.g. by diffing against the rule list.
    for (const newSheet of sheetsToApply) {
      newSheet.disabled = this.disabled;
      this._flushGroup(newSheet, this);
      this._upToDateSheets.add(newSheet);
    }
  }

  /**
   * Flush the current group to the target sheet on the page; this is recursively called for any child CSSGroupingRule we may have
   * @param targetSheet
   * @param group
   */
  private _flushGroup(targetSheet: CSSStyleSheet, group: FsCSSOMSheet | FsCSSOMGroup) {
    const targetParent =
      group instanceof FsCSSOMGroup ? group.parentRule ?? targetSheet : targetSheet;

    for (let i = 0; i < group.cssRules.length; i++) {
      const rule = group.cssRules[i];
      if (!rule) {
        continue;
      }

      switch (rule.type) {
        case 'rule':
          if (rule.cssText) {
            insertStyleRule(targetParent, rule.cssText, i);
          } else if (!targetParent.cssRules[i]) {
            insertStyleRule(targetParent, dummyCssRule, i);
          }

          if (rule.changes && rule.changes.size > 0) {
            const ruleUpdates = rule.changes;

            const selector = targetParent.cssRules[i];
            if (selector.type === CSSRule.STYLE_RULE) {
              const styleRule = selector as CSSStyleRule;
              ruleUpdates.forEach((value, key) => {
                styleRule.style[key as unknown as any] = value;
              });
            } else {
              console.error('Tried to update style rule, but not on style rule!');
            }
          }
          break;
        case 'group':
          if (rule.cssText) {
            insertStyleRule(targetParent, rule.cssText, i);
          } else {
            // we did not detect a css text, check to see if it exists already
            const existingRule = targetParent.cssRules[i];
            if (!existingRule) {
              // if we don't detect an existing rule at where we expect to be a grouping rule, insert a dummy one
              insertStyleRule(targetParent, mediaPlaceholderRule, i);
            }
          }
          rule.parentRule = targetParent.cssRules[i] as CSSGroupingRule;
          this._flushGroup(targetSheet, rule);
          break;
      }
    }
  }

  private _onSheetChange() {
    for (const node of this.ownerNodes) {
      node.onSheetChange();
    }
  }

  private cssCamelToKebabCase(camel: string): string {
    const kebab = camel.replace(/[a-z][A-Z]/g, match => `${match[0]}-${match[1].toLowerCase()}`);

    // Vendor prefixes from: https://www.w3.org/TR/CSS2/syndata.html#vendor-keywords
    const vendorPrefixedRe = /^(?:ms|mso|moz|o|xv|atsc|wap|khtml|webkit|prince|ah|hp|ro|rim|tc)-/;
    if (vendorPrefixedRe.test(kebab)) {
      return `-${kebab}`;
    }
    return kebab;
  }
}

function insertStyleRule(sheet: RuleContainer, rule: string, index?: number) {
  try {
    // Slight hack: We see rare instances of index being past the end of the rule list, triggering an exception,
    // both here and in the catch block. Extend the rules with dummy slots until there's a valid slot for the new one.
    let len = sheet.cssRules ? sheet.cssRules.length : 0;
    if (index !== undefined && len < index) {
      console.log('insert rule index out of bounds; extending sheet to make room');
      while (len < index) {
        sheet.insertRule(dummyCssRule, len);
        len++;
      }
    }

    sheet.insertRule(rule, index);
  } catch (e) {
    console.log(`failed to insert rule: ${e}`);

    // Maybe the rule had an invalid selector? For instance, '.my-class::-moz-placeholder {}' on Chrome.
    // Insert a dummy rule, with the hope of preventing subsequent insertions
    // from throwing IndexSizeError exception due to the index being off by 1.
    try {
      sheet.insertRule(dummyCssRule, index);
    } catch (_) {
      // Probably a HierarchyRequestError
    }
  }
}

export class FsCSSOMGroup {
  constructor(private _cssom: FsCSSOMSheet, public rule: FSCssRule | undefined) {}
  public type: 'group' = 'group';
  private _insertedRules: FSCssRule[] = [];

  get cssRules() {
    return this._insertedRules;
  }

  get cssText(): string {
    return this.rule?.cssText ?? '';
  }

  private _rule?: CSSGroupingRule;

  get parentRule() {
    return this._rule;
  }

  set parentRule(value: CSSGroupingRule | undefined) {
    this._rule = value;
  }

  insertFromRule(next: FSCssRule | FsCSSOMGroup, idx: number): FsCSSOMGroup {
    const group = new FsCSSOMGroup(this._cssom, next);
    this.cssRules[idx] = group;
    return group;
  }

  // FIXME: rewriting the group as a whole is how we test the rewriter e.g.` @media { div {} }`, but that
  // depends on how the rules were added to the page. If they were added as individual rules, we first see
  // `@media {}` and then `div {}` and we don't rewrite the group as a whole. This could lead to weird bugs.
  // The fix is to always rewrite the group as a whole.
  insertRule(cssText: string, index: number | undefined) {
    this._cssom.insertRule(cssText, index, this);
  }

  deleteRule(index: number) {
    this._insertedRules.splice(index, 1);
  }
}
